import React from 'react';
import { useNavigate } from 'react-router';
import {
  useNotificationFeed,
  useMarkSeen,
  useMarkRead,
  useMarkAllRead,
  clearDeliveredNotifications,
} from '@/features/notifications/use-notifications';
import { routeFor } from '@/features/notifications/notification-routing';
import type { NotificationRow } from '@/features/notifications/api';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * Full-page notification feed — the mobile counterpart to the header bell
 * popup (which is cramped on small screens). Reached via the bottom-nav
 * "Notifications" tab. Opening the page marks everything seen (clears the
 * badge); rows stay bold-unread until each is opened. Facebook-style.
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const { data: feed } = useNotificationFeed();
  const markSeen = useMarkSeen();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const rows = feed?.data ?? [];
  const unseen = feed?.unseenCount ?? 0;
  const hasUnread = rows.some((n) => !n.readAt);

  // Clear the badge once when the page opens (and again if a new batch arrives
  // while it's open). markSeen is a no-op server-side when nothing is unseen.
  React.useEffect(() => {
    if (unseen > 0) markSeen.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseen]);

  // Drop this DEVICE's OS notifications whenever the page opens. Deliberately
  // not folded into the markSeen effect above: `unseen` is a server-side,
  // per-user latch, so after the feed has been read anywhere else it is
  // already 0 and that effect fires nothing — while this device's tray still
  // holds the notifications and the launcher keeps its badge.
  //
  // Keyed on the newest row rather than [] so a push that ARRIVES while the
  // driver is sitting on this page clears too: the shell posts it as a tray
  // banner, use-push-routing invalidates the feed, the row appears in the list
  // being looked at — and a mount-only effect would leave the badge behind.
  // Not keyed on `feed` itself, which would re-fire on every 10s poll.
  const newestId = rows[0]?.id;
  React.useEffect(() => {
    void clearDeliveredNotifications();
  }, [newestId]);

  function onItemClick(n: NotificationRow) {
    if (!n.readAt) markRead.mutate(n.id);
    navigate(routeFor(n.kind, n.data.jobId));
  }

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Notifications</h1>
        {hasUnread && (
          <button className="text-sm text-muted-foreground hover:underline" onClick={() => markAllRead.mutate()}>
            Mark all read
          </button>
        )}
      </div>

      {!rows.length ? (
        <div className="rounded-lg border py-16 text-center text-sm text-muted-foreground">
          No notifications
        </div>
      ) : (
        <div className="rounded-lg border divide-y overflow-hidden">
          {rows.map((n) => (
            <button
              key={n.id}
              onClick={() => onItemClick(n)}
              className={`w-full text-left px-4 py-3 hover:bg-accent flex gap-3 cursor-pointer ${n.readAt ? '' : 'bg-accent/40'}`}
            >
              {!n.readAt && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{n.title}</span>
                <span className="block text-sm text-muted-foreground break-words">{n.body}</span>
                <span className="block text-xs text-muted-foreground mt-1">{relativeTime(n.createdAt)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
